
export interface Question {
  id: number | string;
  question: string;
  options: string[];
  correctanswer: number;
  difficulty: "easy" | "medium" | "hard";
  category?: string;
  subject?: string;
  explanation?: string;
  hint?: string;
  question_type: 'mcq' | 'true_false' | 'fill_blank';
  ai_generated?: boolean;
  time_limit?: number;
  shownAtIndex?: number;
  userAnswer?: number | null;
  marks?: number;
  negative_marks?: number;
  microSkills?: string[];
  metadata?: {
    topic?: string;
    section_name?: string;
    source?: string;
    variation_type?: string;
    original_question_id?: number | string;
    exam_name?: string;
    passage?: string;
    [key: string]: any;
  };
  translatedContent?: {
    [language: string]: {
      question: string;
      options: string[];
      explanation?: string;
    }
  };
}

export function normalizeDifficulty(difficulty: string): Question['difficulty'] {
  const normalizedDifficulty = difficulty.toLowerCase();
  switch (normalizedDifficulty) {
    case 'easy':
    case 'medium':
    case 'hard':
      return normalizedDifficulty as Question['difficulty'];
    default:
      return 'medium'; // Default fallback
  }
}

export interface LearningPreferences {
  dailyGoal: number;
  preferredDifficulty: "easy" | "medium" | "hard";
  focusSubjects: string[];
  studyReminders: boolean;
  adaptiveLearning: boolean;
  spaceRepetition: boolean;
  preferredLanguage?: string;
}

export interface QuizState {
  selectedAnswer: number | null;
  currentQuestionIndex: number;
  score: number;
  showHint: boolean;
  category: string | null;
  subject: string | null;
  difficulty: "easy" | "medium" | "hard";
  questionsCount: number;
  isLoading: boolean;
  selectedCategory: string | null;
  selectedCategoryId: number | null;
  selectedSubject: string | null;
  streak: number;
  timeLeft: number;
  darkMode: boolean;
  studyMode: boolean;
  practiceMode: boolean;
  answers: (number | null)[];
  learningPreferences: LearningPreferences;
  language: string;
  view: string;
  questionIndex: number;
  currentHint: string | null;
  completedQuestions: Question[];
  adaptivePool: Question[];
  usedQuestionIds: number[];
  lastSessionId: string | null;
}

export interface QuizResultsData {
  questions: Question[];
  answers: (number | null)[];
  subject: string | null;
  category: string | null;
  difficulty: string;
  score: number;
  total_questions: number;
  percentage: number;
  microSkillsAnalysis?: MicroSkillAnalysis[];
  language?: string;
}

export interface MicroSkillAnalysis {
  skill: string;
  totalQuestions: number;
  correctAnswers: number;
  percentage: number;
  strength: 'strong' | 'moderate' | 'weak';
}

export function categorizeStrength(percentage: number): 'strong' | 'moderate' | 'weak' {
  if (percentage >= 75) return 'strong';
  if (percentage >= 50) return 'moderate';
  return 'weak';
}

export function analyzeMicroSkills(
  questions: Question[], 
  answers: (number | null)[]
): MicroSkillAnalysis[] {
  const skillMap: Record<string, { total: number, correct: number }> = {};
  
  questions.forEach((question, index) => {
    const userAnswer = answers[index];
    const isCorrect = userAnswer === question.correctanswer;
    
    if (question.microSkills && question.microSkills.length > 0) {
      question.microSkills.forEach(skill => {
        if (!skillMap[skill]) {
          skillMap[skill] = { total: 0, correct: 0 };
        }
        
        skillMap[skill].total++;
        if (isCorrect) {
          skillMap[skill].correct++;
        }
      });
    }
  });
  
  return Object.entries(skillMap).map(([skill, data]) => {
    const percentage = data.total > 0 ? (data.correct / data.total) * 100 : 0;
    
    return {
      skill,
      totalQuestions: data.total,
      correctAnswers: data.correct,
      percentage,
      strength: categorizeStrength(percentage)
    };
  }).sort((a, b) => b.percentage - a.percentage);
}
